import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Award, Shield, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { PageHero, ParallaxImage } from '../components/motion/PageHero';
import { Reveal, RevealText } from '../components/motion/Reveal';

const About = () => {
  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <PageHero image="https://images.unsplash.com/photo-1771210353591-20006eba7ad7" eyebrow="Our story" title="About Obsidian" subtitle="Redefining luxury chauffeur services across the United Kingdom" />

      {/* Our Story */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6"><RevealText text="The Ultimate Travel Experience" /></h2>
              <Reveal className="space-y-4 text-white/70 text-lg leading-relaxed">
                <p>
                  Obsidian Chauffeurs was founded with a singular vision: to provide the ultimate travel experience through exceptional service, luxury vehicles, and unparalleled attention to detail.
                </p>
                <p>
                  Based in Bramhall, Cheshire, we serve clients throughout the United Kingdom, offering a comprehensive range of premium chauffeur services. Whether you're travelling for business or pleasure, attending a special event, or need reliable airport transfers, our professional team is dedicated to exceeding your expectations.
                </p>
                <p>
                  Our fleet of Mercedes V-Class vehicles represents the pinnacle of luxury and comfort, while our experienced chauffeurs ensure every journey is smooth, punctual, and memorable.
                </p>
              </Reveal>
            </div>
            <Reveal>
              <ParallaxImage src="https://customer-assets.emergentagent.com/job_56e53924-f98a-4cf8-baa2-84bfe5bc81ba/artifacts/lq15akpi_IMG_6349.JPG" alt="Obsidian Fleet" className="aspect-[4/5] lg:aspect-[4/5]" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 bg-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4"><RevealText text="Our Core Values" /></h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Award,
                title: 'Excellence',
                description: 'We strive for perfection in every aspect of our service'
              },
              {
                icon: Shield,
                title: 'Reliability',
                description: 'Punctual, dependable, and consistently professional'
              },
              {
                icon: Clock,
                title: 'Punctuality',
                description: 'We value your time as much as you do'
              },
              {
                icon: Users,
                title: 'Discretion',
                description: 'Privacy and confidentiality are paramount'
              }
            ].map((value, index) => (
              <Reveal key={index} delay={index * 0.1}><Card className="bg-black text-white border-white/10 p-8 text-center group hover:bg-white/5 hover:-translate-y-1 transition-all duration-300 h-full">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-white/20 transition-all duration-300">
                  <value.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-white/60 text-sm">{value.description}</p>
              </Card></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <ParallaxImage src="https://customer-assets.emergentagent.com/job_56e53924-f98a-4cf8-baa2-84bfe5bc81ba/artifacts/ht5j44ck_C213A9C4-824B-4E9B-A066-1C97C6D0F1F0.JPEG" alt="Interior" className="aspect-[4/5]" />
            </Reveal>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-8"><RevealText text="Why Choose Obsidian?" /></h2>
              <div className="space-y-4">
                {[
                  'Experienced, vetted, and professionally trained chauffeurs',
                  'Luxurious Mercedes V-Class fleet with premium amenities',
                  'Available 24/7 for your convenience',
                  'Comprehensive UK-wide coverage',
                  'Competitive, transparent pricing',
                  'Real-time flight monitoring for airport transfers',
                  'Complimentary WiFi and refreshments',
                  'Immaculately maintained vehicles',
                  'Personalised service tailored to your needs',
                  'Corporate account management available'
                ].map((item, index) => (
                  <Reveal key={index} delay={index * 0.05} className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-white/80">{item}</span>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-24 bg-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4"><RevealText text="Service Coverage" /></h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              Based in Cheshire, serving clients nationwide
            </p>
          </div>

          <Reveal className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-black text-white border-white/10 p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Based in</h3>
              <p className="text-white/70">Bramhall, Cheshire</p>
              <p className="text-sm text-white/50 mt-2">Our home and operational base</p>
            </Card>
            <Card className="bg-black text-white border-white/10 p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Primary Coverage</h3>
              <p className="text-white/70">Manchester, Liverpool, Chester, North West England</p>
              <p className="text-sm text-white/50 mt-2">Our local service area</p>
            </Card>
            <Card className="bg-black text-white border-white/10 p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Extended Coverage</h3>
              <p className="text-white/70">Nationwide UK</p>
              <p className="text-sm text-white/50 mt-2">Long-distance and special bookings</p>
            </Card>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white text-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6"><RevealText text="Experience the Obsidian Difference" /></h2>
          <p className="text-xl text-black/70 mb-12">
            Book your premium chauffeur service today
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking">
              <Button className="bg-black text-white hover:bg-black/90 text-lg px-8 py-6 h-auto">
                Request a Quote
              </Button>
            </Link>
            <Link to="/fleet">
              <Button variant="outline" className="border-black text-black hover:bg-black hover:text-white text-lg px-8 py-6 h-auto">
                View Our Fleet
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
